// React
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from '../ui/button';

// Reducer
import { RootState } from '../../reducers/index';
import { store } from '../../ReduxRoot';

// FutureLab
import { useAcceptInvitationMutation, useDeclineInvitationMutation } from '../../store/api/chatApi';
import { removePendingApprovalChatDetails } from '../../actions/pendingApprovalActions';
import { chatItemInFocus } from '../../actions/chatItemInFocusActions';
import { isSameUserId, toUserId } from '../../utilities/UserId';
import { ModalConfirmation } from '../popup/modalConfirmation';
import { trackMixpanelEvent } from '../../services/mixpanel';

export function ChatListChatRequestOption() {
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const loggedInUserDetails = useSelector((state: RootState) => state.loggedInUserDetails);
	const [displayModalConfirmation, setDisplayModalConfirmation] = React.useState<boolean>(false);
	const [navigateAfterResponse, setNavigateAfterResponse] = React.useState(false);
	const optionStore = { action: 'rejectInvitation' };
	const navigate = useNavigate();
	const [acceptInvitation] = useAcceptInvitationMutation();
	const [declineInvitation] = useDeclineInvitationMutation();

	const acceptChatInvitation = async (navigateToChatRoom = false) => {
		if (navigateToChatRoom) {
			navigate('/chat');
		}

		const currentUserId = toUserId(loggedInUserDetails.id);
		const loggedInUserParticipantId = chatInFocus.participants.find((participant) =>
			isSameUserId(participant.userId, currentUserId)
		);

		const chatDetails = {
			chatId: chatInFocus.id,
			participantId: loggedInUserParticipantId?._id,
		};

		try {
			await acceptInvitation(chatDetails).unwrap();
			store.dispatch(removePendingApprovalChatDetails([chatInFocus]));
			store.dispatch(chatItemInFocus(null));
		} catch (error) {
			console.error('The following error occured: ', error);
		}

		trackMixpanelEvent('Accepted Group Chat Invitation', {
			'Group Name': chatInFocus.name,
			'Group Id': chatInFocus.id,
		});
	};

	const declineChatInvitation = async (navigateToChatRoom = false) => {
		if (navigateToChatRoom) {
			navigate('/chat');
		}

		const currentUserId = toUserId(loggedInUserDetails.id);
		const loggedInUserParticipantId = chatInFocus.participants.find((participant) =>
			isSameUserId(participant.userId, currentUserId)
		);

		const chatDetails = {
			chatId: chatInFocus.id,
			participantId: loggedInUserParticipantId?._id,
		};

		try {
			await declineInvitation(chatDetails).unwrap();
			store.dispatch(removePendingApprovalChatDetails([chatInFocus]));
			store.dispatch(chatItemInFocus(null));
		} catch (error) {
			console.error('The following error occured: ', error);
		}
		trackMixpanelEvent('Rejected Group Chat Invitation', {
			'Group Name': chatInFocus.name,
			'Group Id': chatInFocus.id,
		});
	};

	const openDeclineConfirmation = (navigateToChatRoom: boolean) => {
		setNavigateAfterResponse(navigateToChatRoom);
		setDisplayModalConfirmation(true);
	};

	const renderInvitationActions = (navigateToChatRoom: boolean) => (
		<div className="grid w-full grid-cols-2 gap-3 px-4 pb-5 sm:mx-auto sm:w-80 sm:px-0">
			<Button
				variant="destructive"
				className="w-full text-base font-normal"
				onClick={() => openDeclineConfirmation(navigateToChatRoom)}
			>
				Reject
			</Button>
			<Button className="w-full text-base font-normal" onClick={() => acceptChatInvitation(navigateToChatRoom)}>
				Accept
			</Button>
		</div>
	);

	return (
		<div className="shrink-0 bg-black/65">
			<div className="px-2 py-6 text-center text-base text-white">
				You're invited to the group. Accept to start chat with other groupmates.
			</div>
			<div className="chat-mobile-only">{renderInvitationActions(true)}</div>
			<div className="chat-desktop-only">{renderInvitationActions(false)}</div>
			{displayModalConfirmation && (
				<ModalConfirmation
					displayModalConfirmation={displayModalConfirmation}
					setDisplayModalConfirmation={setDisplayModalConfirmation}
					optionStore={optionStore}
					callBackProceedMethodProp={() => declineChatInvitation(navigateAfterResponse)}
				/>
			)}
		</div>
	);
}
